import {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useState,
  type ReactNode,
} from 'react'
import { toast } from 'sonner'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import {
  fetchUnreadConversationCount,
  fetchUnreadNotificationCount,
} from '@/lib/queries'
import type { GuildMessage, GuildNotification } from '@/types/guild'

interface GuildRealtimeContextType {
  unreadMessages: number
  unreadNotifications: number
  refreshUnread: () => void
}

const GuildRealtimeContext = createContext<GuildRealtimeContextType>({
  unreadMessages: 0,
  unreadNotifications: 0,
  refreshUnread: () => {},
})

/** Fired on window for any incoming guild message (RLS-scoped to my conversations) */
export const GUILD_MESSAGE_EVENT = 'guild-message'

export function GuildRealtimeProvider({ children }: { children: ReactNode }) {
  const { user, guildProfile } = useAuth()
  const [unreadMessages, setUnreadMessages] = useState(0)
  const [unreadNotifications, setUnreadNotifications] = useState(0)

  const refreshUnread = useCallback(() => {
    if (!user || !guildProfile) return
    fetchUnreadConversationCount(user.id)
      .then(setUnreadMessages)
      .catch(() => {})
    fetchUnreadNotificationCount()
      .then(setUnreadNotifications)
      .catch(() => {})
  }, [user, guildProfile])

  useEffect(() => {
    refreshUnread()
  }, [refreshUnread])

  useEffect(() => {
    if (!user || !guildProfile) return

    const channel = supabase
      .channel(`guild-user-${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `recipient_id=eq.${user.id}`,
        },
        payload => {
          const notification = payload.new as GuildNotification
          if (notification.app !== 'guild') return
          toast(notification.title, { description: notification.body ?? undefined })
          setUnreadNotifications(count => count + 1)
        }
      )
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'guild_messages' },
        payload => {
          const message = payload.new as GuildMessage
          window.dispatchEvent(
            new CustomEvent(GUILD_MESSAGE_EVENT, { detail: message })
          )
          if (message.sender_id !== user.id) {
            refreshUnread()
          }
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [user?.id, guildProfile?.user_id]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <GuildRealtimeContext.Provider
      value={{ unreadMessages, unreadNotifications, refreshUnread }}
    >
      {children}
    </GuildRealtimeContext.Provider>
  )
}

export function useGuildRealtime() {
  return useContext(GuildRealtimeContext)
}
